/**
 * Découpage d'une saisie de recherche en mots indépendants.
 *
 * Une recherche par sous-chaîne sur des colonnes séparées ne peut pas retrouver
 * « Patrick Goulet » : aucune colonne ne porte à la fois le prénom et le nom.
 * Chaque mot doit donc être cherché séparément, et tous doivent correspondre —
 * dans n'importe quel ordre.
 *
 * Le nombre de mots est borné : une requête composée d'autant de conditions
 * qu'il y a de mots saisis n'a pas à grandir indéfiniment.
 */
const maxSearchTokens = 5

export function splitSearchTokens(value: string): string[] {
  return value.split(' ').filter(Boolean).slice(0, maxSearchTokens)
}

/**
 * Motif `ilike` pour une colonne de téléphone. Les numéros sont enregistrés
 * avec leur mise en forme (« 450-555-1234 », « (450) 555 1234 ») : un mot
 * composé seulement de chiffres et de séparateurs est cherché chiffre par
 * chiffre, quels que soient les séparateurs entre eux. Tout autre mot garde la
 * recherche par sous-chaîne habituelle.
 */
export function phoneSearchPattern(token: string): string {
  const digits = token.replace(/\D/g, '')
  if (digits.length < 3 || !/^[\d\s+\-.]+$/.test(token)) return `%${token}%`
  return `%${digits.split('').join('%')}%`
}

/** Forme comparable d'un texte cherché à l'écran : minuscules, sans accents (« Gérard » → « gerard »). */
export function foldSearchText(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('fr-CA')
}

export interface SearchHighlight {
  pre: string
  hit: string
  post: string
}

/**
 * Découpe `text` autour du premier mot de `query` qu'il contient, sans tenir
 * compte des accents ni de la casse : « jerome » met en gras « Jérôme ». Les
 * positions sont calculées caractère par caractère, car le pliage NFD peut
 * changer la longueur d'une chaîne.
 */
export function highlightMatch(text: string, query: string): SearchHighlight {
  let folded = ''
  // Pour chaque caractère plié : début et fin du caractère d'origine.
  const starts: number[] = []
  const ends: number[] = []
  let offset = 0
  for (const char of text) {
    const foldedChar = foldSearchText(char)
    // Une entrée par unité UTF-16, comme les positions de `indexOf`.
    starts.push(...Array<number>(foldedChar.length).fill(offset))
    ends.push(...Array<number>(foldedChar.length).fill(offset + char.length))
    folded += foldedChar
    offset += char.length
  }
  for (const token of splitSearchTokens(foldSearchText(query.trim()))) {
    const start = folded.indexOf(token)
    if (start < 0) continue
    const from = starts[start] ?? 0
    const to = ends[start + token.length - 1] ?? text.length
    return { pre: text.slice(0, from), hit: text.slice(from, to), post: text.slice(to) }
  }
  return { pre: text, hit: '', post: '' }
}
